import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiArrowLeft, FiSave, FiCheck, FiX, FiLink, FiLink2,
  FiChevronDown, FiChevronRight, FiFileText, FiLayers,
  FiSearch, FiTrash2, FiInfo, FiZap
} from 'react-icons/fi';
import { fetchContractTemplate, updateContractTemplate } from '../../store/slices/contractTemplatesSlice';
import { parametersAPI } from '../../services/api';
import { sanitizeFieldSchema } from '../../utils/sanitizeSchema';
import { getLocalized } from '../../i18n/getLocalized';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import './ContractTemplateEditor.scss';

// SEC-22 — the template's documentHtml is produced server-side from an
// uploaded Word file and rendered with dangerouslySetInnerHTML. Strip anything
// executable/embedding before it reaches the DOM.
const SANITIZE_OPTS = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

// Escape a value for use inside a double-quoted HTML attribute.
const escapeAttr = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Default contract fields that map to core Contract model fields
// Labels are translation keys resolved at render time via t()
const DEFAULT_FIELDS = [
  { key: 'contractName', labelKey: 'contractTemplates.editor.fields.contractTitle', type: 'text' },
  { key: 'contractDate', labelKey: 'contractTemplates.editor.fields.contractDate', type: 'date' },
  { key: 'startDate', labelKey: 'contractTemplates.editor.fields.startDate', type: 'date' },
  { key: 'endDate', labelKey: 'contractTemplates.editor.fields.endDate', type: 'date' },
  { key: 'signingDate', labelKey: 'contractTemplates.editor.fields.signingDate', type: 'date' },
  { key: 'contractAmount', labelKey: 'contractTemplates.editor.fields.contractValue', type: 'number' },
  { key: 'currency', labelKey: 'contractTemplates.editor.fields.currency', type: 'text' },
  { key: 'counterpartyName', labelKey: 'contractTemplates.editor.fields.counterpartyName', type: 'text' },
  { key: 'counterpartyAddress', labelKey: 'contractTemplates.editor.fields.counterpartyAddress', type: 'text' },
  { key: 'counterpartyCity', labelKey: 'contractTemplates.editor.fields.counterpartyCity', type: 'text' },
  { key: 'counterpartyCountry', labelKey: 'contractTemplates.editor.fields.counterpartyCountry', type: 'text' },
  { key: 'counterpartyPhone', labelKey: 'contractTemplates.editor.fields.counterpartyPhone', type: 'text' },
  { key: 'counterpartyEmail', labelKey: 'contractTemplates.editor.fields.counterpartyEmail', type: 'text' },
  { key: 'counterpartyContactPerson', labelKey: 'contractTemplates.editor.fields.contactPerson', type: 'text' },
  { key: 'priority', labelKey: 'contractTemplates.editor.fields.priority', type: 'text' },
  { key: 'confidentialityLevel', labelKey: 'contractTemplates.editor.fields.confidentialityLevel', type: 'text' },
];

function ContractTemplateEditor() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { current: template, currentLoading } = useSelector(s => s.contractTemplates);

  // Field mapping state
  const [mappings, setMappings] = useState([]);
  const [selectedText, setSelectedText] = useState(null);
  const [activeField, setActiveField] = useState(null);

  // Schema state
  const [schemas, setSchemas] = useState([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState('');
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [schemasLoading, setSchemasLoading] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ default: true, schema: true });
  const [fieldSearch, setFieldSearch] = useState('');
  const docRef = useRef(null);

  // Load template
  useEffect(() => {
    if (id) dispatch(fetchContractTemplate(id));
  }, [dispatch, id]);

  // Seed mappings & schema from loaded template
  useEffect(() => {
    if (template) {
      setMappings(template.mappings || []);
      if (template.fieldSchemaId) {
        const schemaId = typeof template.fieldSchemaId === 'object' ? template.fieldSchemaId._id : template.fieldSchemaId;
        setSelectedSchemaId(schemaId);
      }
    }
  }, [template]);

  // Load available schemas
  useEffect(() => {
    const load = async () => {
      setSchemasLoading(true);
      try {
        const res = await parametersAPI.fieldSchemas.list({ limit: 100 });
        setSchemas(res.data.items || res.data.fieldSchemas || []);
      } catch { /* ignore */ }
      setSchemasLoading(false);
    };
    load();
  }, []);

  // Load full schema when selected
  useEffect(() => {
    if (!selectedSchemaId) { setSelectedSchema(null); return; }
    const load = async () => {
      try {
        const res = await parametersAPI.fieldSchemas.get(selectedSchemaId);
        setSelectedSchema(sanitizeFieldSchema(res.data.item || res.data.fieldSchema || res.data));
      } catch { setSelectedSchema(null); }
    };
    load();
  }, [selectedSchemaId]);

  // Build schema fields list
  const schemaFields = useMemo(() => {
    if (!selectedSchema?.sections) return [];
    const fields = [];
    for (const section of selectedSchema.sections) {
      if (section.type === 'fields' && section.fields) {
        for (const f of section.fields) {
          fields.push({
            key: `${section.id}_${f.id}`,
            label: f.label,
            type: f.type,
            sectionTitle: section.title,
          });
        }
      }
      if (section.type === 'table' && section.tableColumns) {
        for (const col of section.tableColumns) {
          fields.push({
            key: `table_${section.id}_${col.id}`,
            label: `${section.title} > ${col.header}`,
            type: col.type,
            sectionTitle: section.title,
          });
        }
      }
    }
    return fields;
  }, [selectedSchema]);

  // Filter fields by search
  const filteredDefaults = useMemo(() => {
    if (!fieldSearch) return DEFAULT_FIELDS;
    const q = fieldSearch.toLowerCase();
    return DEFAULT_FIELDS.filter(f => t(f.labelKey).toLowerCase().includes(q) || f.key.toLowerCase().includes(q));
  }, [fieldSearch, t]);

  const filteredSchemaFields = useMemo(() => {
    if (!fieldSearch) return schemaFields;
    const q = fieldSearch.toLowerCase();
    return schemaFields.filter(f => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q));
  }, [fieldSearch, schemaFields]);

  // Check if a field is mapped
  const getMappingForField = useCallback((fieldKey) => {
    return mappings.find(m => m.fieldKey === fieldKey);
  }, [mappings]);

  // Handle text selection in the document viewer
  const handleDocumentMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !docRef.current) return;

    const range = selection.getRangeAt(0);
    if (!docRef.current.contains(range.commonAncestorContainer)) return;

    const text = selection.toString().trim();
    if (!text) return;

    // Find paragraph index
    const paragraphs = docRef.current.querySelectorAll('p, h1, h2, h3, h4, li, td');
    let paragraphIndex = 0;
    for (let i = 0; i < paragraphs.length; i++) {
      if (paragraphs[i].contains(range.startContainer)) {
        paragraphIndex = i;
        break;
      }
    }

    // Get context (text before and after selection within the paragraph)
    const paraText = paragraphs[paragraphIndex]?.textContent || '';
    const selStart = paraText.indexOf(text);
    const contextBefore = selStart > 0 ? paraText.substring(Math.max(0, selStart - 50), selStart) : '';
    const contextAfter = selStart >= 0 ? paraText.substring(selStart + text.length, selStart + text.length + 50) : '';

    setSelectedText({
      text,
      paragraphIndex,
      startOffset: range.startOffset,
      endOffset: range.endOffset,
      contextBefore,
      contextAfter,
    });
  }, []);

  // Link selected text to a field
  const linkFieldToSelection = useCallback((fieldKey, fieldLabel, fieldSource) => {
    if (!selectedText) {
      // If there's an active field set, mark it for next selection
      setActiveField({ fieldKey, fieldLabel, fieldSource });
      return;
    }

    const newMapping = {
      fieldKey,
      fieldLabel,
      fieldSource,
      selectedText: selectedText.text,
      paragraphIndex: selectedText.paragraphIndex,
      startOffset: selectedText.startOffset,
      endOffset: selectedText.endOffset,
      contextBefore: selectedText.contextBefore,
      contextAfter: selectedText.contextAfter,
    };

    setMappings(prev => {
      // Replace if already mapped
      const filtered = prev.filter(m => m.fieldKey !== fieldKey);
      return [...filtered, newMapping];
    });

    setSelectedText(null);
    setActiveField(null);
    window.getSelection()?.removeAllRanges();
  }, [selectedText]);

  // Handle clicking a field when activeField mode is not needed (text already selected)
  const handleFieldClick = useCallback((fieldKey, fieldLabel, fieldSource) => {
    if (selectedText) {
      linkFieldToSelection(fieldKey, fieldLabel, fieldSource);
    } else {
      // Toggle active field for "select text next" mode
      if (activeField?.fieldKey === fieldKey) {
        setActiveField(null);
      } else {
        setActiveField({ fieldKey, fieldLabel, fieldSource });
        toast.info(t('contractTemplates.messages.highlightText', { fieldLabel }), { autoClose: 2000 });
      }
    }
  }, [selectedText, activeField, linkFieldToSelection]);

  // When activeField is set and text is selected, auto-link
  useEffect(() => {
    if (activeField && selectedText) {
      linkFieldToSelection(activeField.fieldKey, activeField.fieldLabel, activeField.fieldSource);
    }
  }, [selectedText, activeField, linkFieldToSelection]);

  // Remove a mapping
  const removeMapping = useCallback((fieldKey) => {
    setMappings(prev => prev.filter(m => m.fieldKey !== fieldKey));
  }, []);

  // Save
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await dispatch(updateContractTemplate({
        id,
        data: {
          mappings,
          fieldSchemaId: selectedSchemaId || null,
        },
      })).unwrap();
      toast.success(t('contractTemplates.messages.saveSuccess'));
    } catch (err) {
      toast.error(err || t('contractTemplates.messages.saveFailed'));
    }
    setSaving(false);
  }, [dispatch, id, mappings, selectedSchemaId]);

  // Render highlighted document HTML with mapping markers
  const renderDocumentHtml = useMemo(() => {
    if (!template?.documentHtml) return '';
    // SEC-22: sanitize before any DOM injection.
    let html = DOMPurify.sanitize(template.documentHtml, SANITIZE_OPTS);

    // For each mapping, wrap the matched text with a highlight span
    for (const mapping of mappings) {
      const escapedText = mapping.selectedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedText})`, 'g');
      // Only replace the first occurrence
      let replaced = false;
      // SEC-22: field key/label go through escapeAttr / text-escaping rather
      // than raw concatenation — they come from the schema, not from us.
      const fieldKey = escapeAttr(mapping.fieldKey);
      const fieldLabel = escapeAttr(mapping.fieldLabel);
      html = html.replace(regex, (match) => {
        if (replaced) return match;
        replaced = true;
        return `<span class="cte-highlight" data-field="${fieldKey}" title="${fieldLabel}">${match}<span class="cte-highlight__tag">${fieldLabel}</span></span>`;
      });
    }

    return html;
  }, [template?.documentHtml, mappings]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (currentLoading || !template) {
    return (
      <div className="cte-loading">
        <div className="cte-loading__spinner" />
        <span>{t('contractTemplates.editor.loadingEditor')}</span>
      </div>
    );
  }

  return (
    <div className="cte">
      {/* Top bar */}
      <div className="cte__topbar">
        <div className="cte__topbar-left">
          <button className="cte__back-btn" onClick={() => navigate('/contract-templates')}>
            <FiArrowLeft size={18} />
          </button>
          <div className="cte__topbar-info">
            <h1 className="cte__topbar-title">{template.name}</h1>
            <span className="cte__topbar-meta">
              {t('contractTemplates.editor.fieldsMapped', { count: mappings.length })}
            </span>
          </div>
        </div>
        <div className="cte__topbar-right">
          {selectedText && (
            <div className="cte__selection-badge">
              <FiZap size={12} />
              <span>{t('contractTemplates.editor.textSelectedClickField')}</span>
              <button onClick={() => { setSelectedText(null); window.getSelection()?.removeAllRanges(); }}>
                <FiX size={14} />
              </button>
            </div>
          )}
          <button className="cte__save-btn" onClick={handleSave} disabled={saving}>
            <FiSave size={16} />
            <span>{saving ? t('contractTemplates.editor.saving') : t('contractTemplates.editor.saveTemplate')}</span>
          </button>
        </div>
      </div>

      {/* Main Editor Layout */}
      <div className="cte__body">
        {/* Left Panel — Fields */}
        <div className="cte__panel-left">
          <div className="cte__panel-header">
            <h2>{t('contractTemplates.editor.fieldMappings')}</h2>
            <span className="cte__panel-count">{mappings.length} {t('contractTemplates.editor.mapped')}</span>
          </div>

          {/* Field search */}
          <div className="cte__field-search">
            <FiSearch size={14} />
            <input
              placeholder={t('contractTemplates.editor.searchFields')}
              value={fieldSearch}
              onChange={e => setFieldSearch(e.target.value)}
            />
          </div>

          <div className="cte__fields-scroll">
            {/* Default Contract Fields */}
            <div className="cte__field-group">
              <button
                className="cte__field-group-header"
                onClick={() => toggleSection('default')}
              >
                <FiChevronRight
                  size={14}
                  className={`cte__field-group-chevron ${expandedSections.default ? 'cte__field-group-chevron--open' : ''}`}
                />
                <FiFileText size={14} />
                <span>{t('contractTemplates.editor.defaultContractFields')}</span>
                <span className="cte__field-group-count">{filteredDefaults.length}</span>
              </button>
              {expandedSections.default && (
                <div className="cte__field-list">
                  {filteredDefaults.map(field => {
                    const mapping = getMappingForField(field.key);
                    const isActive = activeField?.fieldKey === field.key;
                    return (
                      <div
                        key={field.key}
                        className={`cte__field-item ${mapping ? 'cte__field-item--mapped' : ''} ${isActive ? 'cte__field-item--active' : ''}`}
                        onClick={() => handleFieldClick(field.key, t(field.labelKey), 'core')}
                      >
                        <div className="cte__field-item-top">
                          <div className="cte__field-item-status">
                            {mapping ? <FiCheck size={12} /> : <FiLink2 size={12} />}
                          </div>
                          <div className="cte__field-item-info">
                            <span className="cte__field-item-label">{t(field.labelKey)}</span>
                            <span className="cte__field-item-key">{field.key}</span>
                          </div>
                        </div>
                        {mapping && (
                          <div className="cte__field-item-preview">
                            <span className="cte__field-item-text">"{mapping.selectedText}"</span>
                            <button
                              className="cte__field-item-remove"
                              onClick={e => { e.stopPropagation(); removeMapping(field.key); }}
                              title={t('contractTemplates.editor.removeMapping')}
                            >
                              <FiTrash2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Schema Selector */}
            <div className="cte__field-group">
              <div className="cte__schema-selector">
                <label>
                  <FiLayers size={14} />
                  <span>{t('contractTemplates.editor.schemaFields')}</span>
                </label>
                <select
                  value={selectedSchemaId}
                  onChange={e => setSelectedSchemaId(e.target.value)}
                >
                  <option value="">{t('contractTemplates.editor.selectSchema')}</option>
                  {schemas.map(s => (
                    <option key={s._id} value={s._id}>{getLocalized(s.name, i18n.language)}</option>
                  ))}
                </select>
              </div>

              {selectedSchema && (
                <>
                  <button
                    className="cte__field-group-header"
                    onClick={() => toggleSection('schema')}
                  >
                    <FiChevronRight
                      size={14}
                      className={`cte__field-group-chevron ${expandedSections.schema ? 'cte__field-group-chevron--open' : ''}`}
                    />
                    <FiLayers size={14} />
                    <span>{selectedSchema.name}</span>
                    <span className="cte__field-group-count">{filteredSchemaFields.length}</span>
                  </button>
                  {expandedSections.schema && (
                    <div className="cte__field-list">
                      {filteredSchemaFields.map(field => {
                        const mapping = getMappingForField(field.key);
                        const isActive = activeField?.fieldKey === field.key;
                        return (
                          <div
                            key={field.key}
                            className={`cte__field-item ${mapping ? 'cte__field-item--mapped' : ''} ${isActive ? 'cte__field-item--active' : ''}`}
                            onClick={() => handleFieldClick(field.key, field.label, 'schema')}
                          >
                            <div className="cte__field-item-top">
                              <div className="cte__field-item-status">
                                {mapping ? <FiCheck size={12} /> : <FiLink2 size={12} />}
                              </div>
                              <div className="cte__field-item-info">
                                <span className="cte__field-item-label">{field.label}</span>
                                <span className="cte__field-item-key">{field.key}</span>
                              </div>
                            </div>
                            {mapping && (
                              <div className="cte__field-item-preview">
                                <span className="cte__field-item-text">"{mapping.selectedText}"</span>
                                <button
                                  className="cte__field-item-remove"
                                  onClick={e => { e.stopPropagation(); removeMapping(field.key); }}
                                  title={t('contractTemplates.editor.removeMapping')}
                                >
                                  <FiTrash2 size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {filteredSchemaFields.length === 0 && (
                        <div className="cte__field-empty">{t('contractTemplates.editor.noFieldsInSchema')}</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="cte__instructions">
            <FiInfo size={14} />
            <div>
              <strong>{t('contractTemplates.editor.howToMapFields')}</strong>
              <ol>
                <li>{t('contractTemplates.editor.step1')}</li>
                <li>{t('contractTemplates.editor.step2')}</li>
                <li>{t('contractTemplates.editor.step3')}</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Right Panel — Document Viewer */}
        <div className="cte__panel-right">
          <div className="cte__doc-header">
            <FiFileText size={16} />
            <span>{template.originalDocument?.originalName || t('contractTemplates.editor.document')}</span>
          </div>
          <div
            className="cte__doc-viewer"
            ref={docRef}
            onMouseUp={handleDocumentMouseUp}
            dangerouslySetInnerHTML={{ __html: renderDocumentHtml }}
          />
        </div>
      </div>
    </div>
  );
}

export default ContractTemplateEditor;
